import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../integrations/supabase/client';

const { FiPhone, FiMail, FiMapPin, FiHeart, FiFacebook, FiInstagram, FiTwitter, FiLock } = FiIcons;

const Footer = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const [content, setContent] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      const { data } = await supabase.from('page_content').select('*').eq('page', 'common').eq('section', 'footer');
      if (data) {
        const contentMap = {};
        data.forEach(item => {
          contentMap[item.key] = isRTL ? (item.content_ar || item.content_en) : item.content_en;
        });
        setContent(contentMap);
      }
    };
    fetchContent();
  }, [isRTL]);

  const getText = (key, fallbackKey) => content?.[key] || t(fallbackKey);

  const footerLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.products'), path: '/products' },
    { name: t('nav.distribution'), path: '/distribution' },
    { name: t('nav.contact'), path: '/contact' }
  ];

  const handleAdminClick = (e) => {
    e.preventDefault();
    navigate('/admin');
    window.scrollTo(0, 0); 
  };

  return (
    <footer className="bg-gradient-to-b from-green-800 to-green-900 text-white relative z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Brand Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2 space-y-6"
          >
            <div className={`flex items-center space-x-3 mb-4 ${isRTL ? 'space-x-reverse' : ''}`}>
              <div className="bg-white/90 rounded-full p-1 shadow-md">
                <img 
                  src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759845307731-logo%20adjusted.png" 
                  alt="Bondeega Coffee Logo" 
                  className="w-12 h-12 object-contain"
                />
              </div>
              <div>
                <h3 className="text-2xl font-bold">Bondeega</h3>
                <p className="text-green-200">بُنديجا</p>
              </div>
            </div>
            
            <p className="text-green-100 leading-relaxed max-w-md">
              {getText('about_text', 'footer.aboutText')}
            </p>

            <div className="space-y-3">
              <div className={`flex items-center space-x-3 ${isRTL ? 'space-x-reverse' : ''}`}>
                <SafeIcon icon={FiPhone} className="w-5 h-5 text-green-300" />
                <a 
                  href="tel:+201031512020" 
                  className="text-green-100 hover:text-white transition-colors" 
                  dir="ltr"
                >
                  +20 103 151 2020
                </a>
              </div>
              <div className={`flex items-center space-x-3 ${isRTL ? 'space-x-reverse' : ''}`}>
                <SafeIcon icon={FiMail} className="w-5 h-5 text-green-300" />
                <a 
                  href="mailto:info@nextsupplytd.com" 
                  className="text-green-100 hover:text-white transition-colors"
                >
                  info@nextsupplytd.com
                </a>
              </div>
              <div className={`flex items-center space-x-3 ${isRTL ? 'space-x-reverse' : ''}`}>
                <SafeIcon icon={FiMapPin} className="w-5 h-5 text-green-300" />
                <span className="text-green-100">{t('distribution.addressText')}</span>
              </div>
            </div>

            <div className={`flex items-center space-x-6 ${isRTL ? 'space-x-reverse' : ''}`}>
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-white/90 to-white/70 rounded-full blur-sm group-hover:blur-md transition-all duration-300"></div>
                <div className="absolute inset-0 bg-white/80 rounded-full backdrop-blur-sm"></div>
                
                <div className="relative bg-white/90 rounded-full p-2 shadow-md backdrop-blur-sm border border-white/50">
                  <img 
                    src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759920909971-NEXT%20SUPPLY%20PNG.png" 
                    alt="Next Supply Logo" 
                    className="w-16 h-16 object-contain"
                  />
                </div>
              </div>
              <div>
                <p className="font-semibold text-white">
                  {isRTL ? 'نكست سابلاي للتجارة والتوزيع' : 'Next Supply Trading & Distribution'}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h4 className="text-xl font-bold mb-4">{t('footer.quickLinks')}</h4>
            <ul className="space-y-2">
              {footerLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-green-200 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
              <li>
                <button 
                  onClick={handleAdminClick}
                  className="text-green-200 hover:text-white transition-colors flex items-center gap-2 mt-4 text-sm opacity-70 hover:opacity-100 cursor-pointer"
                >
                  <SafeIcon icon={FiLock} className="w-3 h-3" />
                  Admin Login
                </button>
              </li>
            </ul>
          </motion.div>

          {/* Social Media */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h4 className="text-xl font-bold mb-4">{t('footer.followUs')}</h4>
            <div className={`flex space-x-4 ${isRTL ? 'space-x-reverse' : ''}`}>
              <a
                href="https://www.facebook.com/profile.php?id=61581742165961"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-700 hover:bg-green-600 p-3 rounded-full transition-colors"
                aria-label="Facebook"
              >
                <SafeIcon icon={FiFacebook} className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/nextsupplytd/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-700 hover:bg-green-600 p-3 rounded-full transition-colors"
                aria-label="Instagram"
              >
                <SafeIcon icon={FiInstagram} className="w-5 h-5" />
              </a>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-green-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-green-200 text-sm text-center md:text-left">
              {getText('copyright', 'footer.copyright')}
            </p>
            <div className={`flex items-center space-x-2 text-green-200 text-sm ${isRTL ? 'space-x-reverse' : ''}`}>
              <span>{isRTL ? 'صنع بـ' : 'Made with'}</span>
              <SafeIcon icon={FiHeart} className="w-4 h-4 text-red-400" />
              <span>{isRTL ? 'في مصر' : 'in Egypt'}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;